import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getQuarterOptions } from "@/lib/fiscal-year";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateQuarterlyReview } from "@/hooks/use-quarterly-reviews";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useUsers } from "@/hooks/use-users";
import { FormError } from "@/components/shared/form-error";
import { YEAR_OPTIONS, DEFAULT_YEAR } from "@/lib/year-options";
import { compareUsersByLastName, formatNameLastFirst } from "@/lib/format-name";

const createReviewSchema = z.object({
  employee_id: z.string().min(1, "Please select an employee"),
  year: z.number().int().min(2020).max(2100),
  quarter: z.enum(["Q1", "Q2", "Q3", "Q4"]),
});

type CreateReviewForm = z.infer<typeof createReviewSchema>;

export function CreateReviewDialog() {
  const [open, setOpen] = useState(false);
  const { currentUser } = useCurrentUser();
  const createReview = useCreateQuarterlyReview();

  const isAdmin = currentUser?.role === "admin";
  const { data: usersData } = useUsers(
    currentUser
      ? isAdmin
        ? { limit: 100 }
        : { team_of: currentUser.id, limit: 100 }
      : { limit: 0 },
  );
  const employeeOptions = [...(usersData?.data ?? [])]
    .filter((u) => u.id !== currentUser?.id)
    .sort(compareUsersByLastName);

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateReviewForm>({
    resolver: zodResolver(createReviewSchema),
    defaultValues: {
      employee_id: "",
      year: DEFAULT_YEAR,
      quarter: "Q1",
    },
  });

  const selectedEmployeeId = watch("employee_id");
  const selectedYear = watch("year");
  const hasEmployees = employeeOptions.length > 0;

  const onSubmit = (data: CreateReviewForm) => {
    if (!currentUser) return;
    createReview.mutate(
      {
        employee_id: data.employee_id,
        supervisor_id: currentUser.id,
        year: data.year,
        quarter: data.quarter,
      },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>New Review</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Quarterly Review</DialogTitle>
          <DialogDescription>
            Start a quarterly review for one of your subordinates. You will be assigned
            as the supervisor.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Employee</Label>
            <Select
              value={selectedEmployeeId || undefined}
              onValueChange={(v) => setValue("employee_id", v, { shouldValidate: true })}
              disabled={!hasEmployees}
            >
              <SelectTrigger>
                <SelectValue placeholder={hasEmployees ? "Select an employee" : "No subordinates found"} />
              </SelectTrigger>
              <SelectContent>
                {employeeOptions.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {formatNameLastFirst(u.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormError message={errors.employee_id?.message} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Year</Label>
              <Select
                value={selectedYear.toString()}
                onValueChange={(v) => setValue("year", parseInt(v, 10))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormError message={errors.year?.message} />
            </div>

            <div className="space-y-2">
              <Label>Quarter</Label>
              <Select
                defaultValue="Q1"
                onValueChange={(v) => setValue("quarter", v as "Q1" | "Q2" | "Q3" | "Q4")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getQuarterOptions(selectedYear).map((q) => (
                    <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createReview.isPending || !hasEmployees}>
              {createReview.isPending ? "Creating..." : "Create Review"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
